import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import moment from 'moment-timezone';

import WebinarRootContainer from './components/WebinarRootContainer';
import DetailsContainer from './components/DetailsContainer';
import WatchNowButton from './components/WatchNowButton';
import ComingSoonButton from './components/ComingSoonButton';
import SessionEndedButton from './components/SessionEndedButton';
import SpeakerList from './SpeakerList';

import { BREAKPOINT } from './constants';

const TimelineIndicatorContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (min-width: ${BREAKPOINT}px) {
    width: 100px;
    top: 3px;
    left: -125.5px;
  }

  @media (max-width: ${BREAKPOINT}px) {
    top: -27px;
  }
`;

const TimelineIndicatorText = styled.div`
  flex: 0 0 auto;
  text-align: center;

  @media (min-width: ${BREAKPOINT}px) {
    font-weight: bold;
    font-size: 18px;
    line-height: 18px;
    letter-spacing: -0.408px;
    color: #0CA58B;
  }

  @media (max-width: ${BREAKPOINT}px) {
    font-size: 14px;
    line-height: 22px;
    letter-spacing: 0.3px;
    color: #282B40;
  }
`;

const TimelineIndicatorIcon = styled.div`
  background-color: #0CA58B;
  height: 8px;
  width: 8px;
  border-radius: 4px;
  margin-left: 12px;

  @media (max-width: ${BREAKPOINT}px) {
    display: none;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 960px) {
    justify-content: space-between;
  }
`;

const DateTimeContainer = styled.div`
  font-style: normal;
  font-weight: bold;

  @media (min-width: 960px) {
    font-size: 16px;
    line-height: 19px;
  }

  @media (max-width: 960px) {
    font-size: 14px;
    line-height: 17px;
  }
`;

const LiveTag = styled.div`
  background: #F77;
  border-radius: 2px;
  padding: 3.5px 10px;
  margin-left: 16px;
  font-weight: bold;
  font-size: 14px;
  line-height: 17px;
  color: #FFF;
  text-transform: uppercase;
`;

const NameContainer = styled.div`
  font-style: normal;
  font-weight: bold;

  @media (min-width: 960px) {
    font-size: 24px;
    line-height: 29px;
    margin: 16px 221px 16px 0;
  }

  @media (max-width: 960px) {
    font-size: 18px;
    line-height: 22px;
    margin: 16px 0;
  }
`;

const LiveWebinarItem = ({
  id,
  name,
  startAt,
  endAt,
  timezone,
  details,
  url,
  cfStreamId,
  speakers,
  showTimelineIndicator,
  timelineIndicatorText,
}) => {
  const startTime = moment.tz(startAt, timezone);
  const endTime = moment.tz(endAt, timezone);

  const durationInMins = Math.abs(moment.duration(startTime.diff(endTime)).asMinutes());

  const currentTime = moment();
  const isComingSoon = currentTime.isBefore(startTime);
  const isEnded = currentTime.isAfter(endTime);
  const isLive = !isComingSoon && !isEnded;

  return (
    <WebinarRootContainer>
      {
        showTimelineIndicator && (
          <TimelineIndicatorContainer>
            <TimelineIndicatorText>
              {timelineIndicatorText}
            </TimelineIndicatorText>
            <TimelineIndicatorIcon />
          </TimelineIndicatorContainer>
        )
      }
      <FlexContainer>
        <DateTimeContainer>
          {
            `${startTime.format('h:mmA')} - ${endTime.format('h:mmA')} (${startTime.zoneName()} | GMT${startTime.format('Z')}) (${durationInMins} mins)`
          }
        </DateTimeContainer>
        {
          isLive && (
            <LiveTag>
              Live
            </LiveTag>
          )
        }
      </FlexContainer>
      <NameContainer>
        { name }
      </NameContainer>
      <DetailsContainer>
        { ReactHtmlParser(details) }
      </DetailsContainer>
      <SpeakerList speakers={speakers} />
      {!!(cfStreamId || url) && (<WatchNowButton id={id} name={name} from="LiveWebinar" url={url} cfStreamId={cfStreamId} />)}
    </WebinarRootContainer>
  );
};

LiveWebinarItem.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  startAt: PropTypes.string.isRequired,
  endAt: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  cfStreamId: PropTypes.string.isRequired,
  details: PropTypes.string,
  url: PropTypes.string.isRequired,
  speakers: PropTypes.arrayOf(PropTypes.shape()),
  showTimelineIndicator: PropTypes.bool,
  timelineIndicatorText: PropTypes.string,
};

LiveWebinarItem.defaultProps = {
  speakers: [],
  details: '',
  showTimelineIndicator: false,
  timelineIndicatorText: '',
};

export default LiveWebinarItem;
